// data/faqContent.ts
// FAQ content for /faq page — SSR via Vike
// Source of truth for all FAQ questions, answers, and schema markup
// Last updated: 2026-02-05

export interface FAQItem {
  id: string;
  question: string;
  answer: string; // HTML allowed for links, bold, lists
  linkText?: string; // e.g. "Browse verified properties"
  linkHref?: string; // e.g. "/for-sale" — only set if page EXISTS
}

export interface FAQCategory {
  id: string;
  title: string;
  intro: string;
  items: FAQItem[];
}

export const FAQ_META = {
  title: 'Frequently Asked Questions | Afriqahome',
  description:
    'Answers to common questions about buying property in Kenya — title deed verification, stamp duty, agent verification, diaspora buying, and more.',
  canonical: 'https://www.afriqahome.com/faq',
  h1: 'Frequently Asked Questions',
  subtitle: 'Everything you need to know about buying property in Kenya',
} as const;

export const FAQ_CATEGORIES: FAQCategory[] = [
  // ─────────────────────────────────────────────
  // 1. TRUST & SAFETY
  // ─────────────────────────────────────────────
  {
    id: 'trust-safety',
    title: 'Trust & Safety',
    intro:
      'Buying property in Kenya comes with real challenges — fraud, fake documents, and unverified agents. Here\u2019s how to protect yourself, and how Afriqahome helps.',
    items: [
      {
        id: 'verify-title-deed',
        question: 'How do I verify a title deed in Kenya?',
        answer:
          'Conduct an official land search through <strong>Ardhisasa</strong> (ardhisasa.lands.go.ke), Kenya\u2019s digital land registry. Create an account, enter the parcel number, and pay KES\u00A0500 via M-Pesa. You\u2019ll receive a search certificate showing the registered owner, property size, and any encumbrances like mortgages or court orders.<br><br>For areas not yet on Ardhisasa, visit the county land registry in person and submit Form RL\u00A026. Processing takes one to three business days.<br><br>Never rely on a physical title deed alone \u2014 forgeries exist. An official search is the only definitive verification.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'common-scams',
        question: 'What are the most common property scams in Kenya?',
        answer:
          'The most dangerous scams include:<br><br><strong>Double-selling</strong> \u2014 the same property sold to multiple buyers before any transfer is registered.<br><strong>Fake title deeds</strong> \u2014 forged documents with convincing stamps and watermarks.<br><strong>Impersonation fraud</strong> \u2014 someone poses as the rightful owner using forged identification.<br><strong>Ghost plots</strong> \u2014 land that exists on paper but is government-owned, road reserve, or riparian.<br><strong>Off-plan fraud</strong> \u2014 developers collect deposits for projects that never break ground.<br><br>Kenya records over 3,000 land fraud cases annually. The single most effective protection is conducting an official land search before paying any money.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'spot-fake-title',
        question: 'How can I spot a fake title deed?',
        answer:
          'Warning signs include mismatched fonts or ink colors, spelling errors, missing official watermarks, and parcel numbers that don\u2019t match registry records. Some forgeries are nearly indistinguishable from genuine documents.<br><br>The only reliable verification is an official land search through Ardhisasa or the county land registry. Never trust a physical document alone \u2014 always verify against government records before any payment.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'pay-before-viewing',
        question: 'Should I pay money before viewing a property?',
        answer:
          'No. Never pay any money \u2014 including \u201Cviewing fees,\u201D \u201Cbooking fees,\u201D or \u201Creservation deposits\u201D \u2014 before physically visiting the property and independently verifying ownership.<br><br>Legitimate sellers and agents do not require upfront payments before viewings. If you\u2019re pressured for money before seeing the property, walk away. This is one of the most common scam patterns in the Kenyan market.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'how-afriqahome-verifies',
        question: 'How does Afriqahome verify agents?',
        answer:
          'Agents on Afriqahome go through a verification process before their listings appear on the platform. This includes identity verification, business documentation review, and listing quality checks.<br><br>Verified agents display a trust badge on their profile. However, Afriqahome is a marketplace \u2014 we connect buyers with agents but do not guarantee individual transactions. Always conduct your own due diligence on any property, regardless of the platform.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'report-fraudulent-listing',
        question: 'What should I do if I suspect a fraudulent listing?',
        answer:
          'Contact us immediately by email at support@afriqahome.com with the listing URL and details of your concern. We investigate all reports and remove listings that violate our policies.<br><br>If you believe you\u2019ve been a victim of fraud, file a report with the nearest police station and the Directorate of Criminal Investigations (DCI). For land-related disputes, you can also file a complaint with the National Land Commission.',
      },
      {
        id: 'off-plan-safety',
        question: 'Is it safe to buy off-plan property in Kenya?',
        answer:
          'Off-plan purchases carry additional risk because you\u2019re paying for a property that doesn\u2019t exist yet. To protect yourself:<br><br>\u2022 Verify the developer\u2019s track record \u2014 have they completed previous projects?<br>\u2022 Confirm the land title and approved building plans with the county government<br>\u2022 Ensure payments go into an escrow or advocate\u2019s client account, not a personal account<br>\u2022 Check that the sale agreement includes clear timelines, penalties for delays, and refund terms<br><br>Off-plan can offer lower entry prices, but only if you do thorough due diligence on the developer and the project.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'use-lawyer',
        question: 'Should I use a lawyer when buying property in Kenya?',
        answer:
          'Yes. A conveyancing lawyer is not optional \u2014 they\u2019re essential protection. Your lawyer conducts the official land search, drafts or reviews the sale agreement, handles stamp duty payment, and manages the title transfer process.<br><br>Expect to pay 1\u20132% of the purchase price for legal fees (minimum approximately KES\u00A035,000 plus 16% VAT). This is one of the best investments you\u2019ll make in the entire transaction.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'visit-in-person',
        question: 'Do I need to visit the property in person?',
        answer:
          'Strongly recommended, especially for land purchases. Photos can be misleading \u2014 you need to verify the physical boundaries match the survey plan, check for encroachment by neighbors, assess access roads, and confirm the plot isn\u2019t on a flood plain, riparian reserve, or road reserve.<br><br>If you\u2019re buying from abroad, send a trusted representative (ideally your lawyer) to physically inspect the property and take geotagged photos and video.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 2. COSTS & FEES
  // ─────────────────────────────────────────────
  {
    id: 'costs-fees',
    title: 'Costs & Fees',
    intro:
      'Property transactions in Kenya involve costs beyond the purchase price. Here\u2019s what to budget for so nothing catches you off guard.',
    items: [
      {
        id: 'stamp-duty',
        question: 'How much is stamp duty on property in Kenya?',
        answer:
          'Stamp duty is a government tax paid when transferring property ownership:<br><br><strong>4% of the property value</strong> in urban areas (Nairobi, Mombasa, Kisumu, and most gazetted municipalities).<br><strong>2% of the property value</strong> in rural areas.<br><br>Since April 2024, many previously rural areas \u2014 including parts of Kiambu, Kitengela, Nakuru, and Machakos \u2014 were reclassified as urban and now attract the 4% rate.<br><br>The buyer pays stamp duty within 30 days of executing the transfer documents. Your lawyer handles the payment through KRA\u2019s iTax system.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'total-buying-costs',
        question: 'What are the total costs of buying property in Kenya?',
        answer:
          'Budget 6\u201310% of the purchase price for transaction costs on top of the sale price:<br><br><strong>Stamp duty:</strong> 2\u20134% of property value<br><strong>Legal/conveyancing fees:</strong> 1\u20132% (min ~KES\u00A035,000 + VAT)<br><strong>Official land search:</strong> KES\u00A0500\u20131,000<br><strong>Valuation report:</strong> KES\u00A015,000\u201350,000<br><strong>Registration fees:</strong> ~KES\u00A05,000<br><br>For a KES\u00A010 million property in Nairobi, expect approximately KES\u00A0600,000\u2013800,000 in total transaction costs. If you\u2019re financing with a mortgage, add bank valuation and processing fees.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'lawyer-fees',
        question: 'How much do property lawyers charge in Kenya?',
        answer:
          'Conveyancing lawyers typically charge 1\u20132% of the purchase price with a minimum fee of approximately KES\u00A035,000, plus 16% VAT. For properties above KES\u00A010 million, rates often negotiate down to 1\u20131.5%.<br><br>The fee covers: conducting the official land search, reviewing or drafting the sale agreement, handling stamp duty payment, executing the transfer, and registering the new title deed.<br><br>Get fee quotes from two or three lawyers before engaging. Ensure the quote covers the full process, not just the agreement stage.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'capital-gains-tax',
        question: 'Is there capital gains tax on property sales in Kenya?',
        answer:
          'Yes. Capital gains tax in Kenya is 15% on the profit (sale price minus purchase price and allowable expenses). It\u2019s paid by the seller within 30 days of the transfer.<br><br>Allowable deductions include the original purchase price, stamp duty paid on acquisition, legal fees, and documented improvement costs. Your lawyer or tax advisor can help calculate the exact liability.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'who-pays-stamp-duty',
        question: 'Who pays stamp duty \u2014 buyer or seller?',
        answer:
          'The buyer pays stamp duty. This is standard practice in Kenya and is typically a condition in the sale agreement. Your lawyer remits the payment to KRA through the iTax portal before the transfer can be registered.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'stamp-duty-exemptions',
        question: 'Are there any stamp duty exemptions in Kenya?',
        answer:
          'Yes, some transfers are exempt:<br><br>\u2022 Transfers between spouses<br>\u2022 Certain transfers involving registered self-help groups<br>\u2022 Diplomatic property transfers<br>\u2022 Some government-to-government transfers<br><br>First-time homebuyer exemptions do not currently exist in Kenya. If you\u2019re unsure whether an exemption applies, consult a conveyancing lawyer.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 3. BUYING PROCESS
  // ─────────────────────────────────────────────
  {
    id: 'buying-process',
    title: 'Buying Process',
    intro:
      'Buying property in Kenya takes 30\u201390 days from agreement to title deed. Here\u2019s every step, so you know exactly what to expect.',
    items: [
      {
        id: 'steps-to-buy',
        question: 'What are the steps to buy property in Kenya?',
        answer:
          'The process follows these stages:<br><br>1. <strong>Identify the property</strong> \u2014 search, view, and compare options<br>2. <strong>Conduct a land search</strong> \u2014 verify ownership via Ardhisasa (KES\u00A0500)<br>3. <strong>Negotiate terms and sign a sale agreement</strong> \u2014 typically with a 10% deposit<br>4. <strong>Obtain Land Control Board consent</strong> \u2014 required for agricultural and some freehold land<br>5. <strong>Pay stamp duty</strong> \u2014 4% urban, 2% rural<br>6. <strong>Execute transfer documents</strong> \u2014 both parties sign at the lands office<br>7. <strong>Submit for registration</strong> \u2014 documents lodged at the county land registry<br>8. <strong>Receive new title deed</strong> \u2014 issued in the buyer\u2019s name<br><br>Total timeline: 30\u201390 days depending on complexity. Agricultural land or properties with succession issues take longer.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'freehold-vs-leasehold',
        question: 'What is the difference between freehold and leasehold in Kenya?',
        answer:
          '<strong>Freehold</strong> means you own the land indefinitely with no time limit. It\u2019s the strongest form of ownership and can be passed down through generations. Most privately owned land in Kenya\u2019s rural and peri-urban areas is freehold.<br><br><strong>Leasehold</strong> means you own the land for a set period \u2014 typically 99 years \u2014 granted by the government. Common in Nairobi and other urban areas. When the lease expires, you apply to the National Land Commission for renewal.<br><br>Both types can be bought, sold, and mortgaged. The key difference is duration of ownership.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'land-control-board',
        question: 'What is Land Control Board consent and when do I need it?',
        answer:
          'The Land Control Board (LCB) is a government body that must approve certain land transactions. Consent is required for:<br><br>\u2022 Agricultural land (anywhere in Kenya)<br>\u2022 Some freehold land transactions outside major urban centers<br><br>Apply through the local Land Control Board with: proof of ownership, signed sale agreement, copies of both parties\u2019 IDs, and a valuation report. Processing takes approximately two to four weeks. Cost: KES\u00A01,000 application fee plus board member costs.<br><br>Transactions without LCB consent where required are void \u2014 the transfer will not be registered.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'transfer-timeline',
        question: 'How long does the property transfer process take in Kenya?',
        answer:
          '<strong>Simple urban transfer</strong> (apartment, titled land): 30\u201360 days<br><strong>Agricultural land</strong> (requires LCB consent): 60\u201390 days<br><strong>Succession-involved property:</strong> 90+ days (requires court grant of letters of administration)<br><strong>Off-plan purchase:</strong> depends entirely on the developer\u2019s construction timeline<br><br>Delays typically come from: incomplete documentation, pending consents, disputes that surface during the search, or slow processing at the lands registry.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'documents-needed',
        question: 'What documents do I need to buy property in Kenya?',
        answer:
          'As a buyer, you\u2019ll need:<br><br>\u2022 National ID or passport<br>\u2022 KRA PIN certificate (get one free at itax.kra.go.ke)<br>\u2022 Recent passport-size photos<br>\u2022 Proof of funds or mortgage pre-approval letter<br>\u2022 eCitizen account (for Ardhisasa and government services)<br><br>Your lawyer handles the land search certificate, sale agreement, transfer documents, stamp duty assessment, and registration forms.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'what-is-conveyancing',
        question: 'What is conveyancing?',
        answer:
          'Conveyancing is the legal process of transferring property ownership from seller to buyer. In Kenya, it covers everything from the initial land search and agreement drafting through to the registration of the new title deed in your name.<br><br>A licensed conveyancing lawyer manages this process. It typically costs 1\u20132% of the property value and takes 30\u201390 days to complete.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'how-to-land-search',
        question: 'How do I conduct a land search in Kenya?',
        answer:
          '<strong>Online via Ardhisasa (recommended):</strong><br>1. Register at ardhisasa.lands.go.ke<br>2. Navigate to \u201CServices\u201D \u2192 \u201COfficial Search\u201D<br>3. Enter the title number or parcel number<br>4. Pay KES\u00A0500 via M-Pesa<br>5. Download the search certificate<br><br><strong>In person at the land registry:</strong><br>1. Visit the county land registry where the property is registered<br>2. Fill in Form RL\u00A026<br>3. Pay the search fee<br>4. Collect the results (one to three business days)<br><br>The search reveals: registered owner, property size, any mortgages or charges, caveats, and court orders.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 4. DIASPORA BUYERS
  // ─────────────────────────────────────────────
  {
    id: 'diaspora-buyers',
    title: 'For Diaspora Buyers',
    intro:
      'Buying property in Kenya from abroad is possible \u2014 and increasingly common. The challenge isn\u2019t finding property; it\u2019s trusting the process from thousands of miles away. Here\u2019s how to protect yourself.',
    items: [
      {
        id: 'buy-from-abroad',
        question: 'Can I buy property in Kenya while living abroad?',
        answer:
          'Yes. Kenyan citizens abroad can purchase both freehold and leasehold property with full ownership rights, same as residents. There are no restrictions on Kenyan nationals buying property regardless of where they live.<br><br>You\u2019ll need: a valid Kenyan ID or passport, a KRA PIN, and an eCitizen account. Most verification steps can be done online through Ardhisasa. For the signing and transfer, grant Power of Attorney to your lawyer.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'verify-from-overseas',
        question: 'How do I verify property from overseas?',
        answer:
          'Start with <strong>Ardhisasa</strong> (ardhisasa.lands.go.ke) \u2014 you can conduct official land searches online from anywhere in the world using your Kenyan ID and M-Pesa. The search certificate confirms ownership, size, and encumbrances.<br><br>Supplement with:<br>\u2022 <strong>Google Maps/Earth</strong> \u2014 verify the location matches what the seller claims<br>\u2022 <strong>Physical site visit</strong> \u2014 send your lawyer or a trusted representative to inspect, photograph, and take GPS coordinates<br>\u2022 <strong>County government check</strong> \u2014 confirm approved building plans and zoning compliance<br>\u2022 <strong>Neighbor verification</strong> \u2014 your representative can speak with adjacent landowners<br><br>Never rely solely on photos or documents sent by the seller.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'power-of-attorney',
        question: 'What is Power of Attorney and do I need one?',
        answer:
          'Power of Attorney (POA) is a legal document that authorizes someone \u2014 usually your conveyancing lawyer \u2014 to sign documents and act on your behalf in a property transaction.<br><br>You need a POA if you cannot be physically present in Kenya for the signing of the transfer documents. It must be:<br>\u2022 In writing and notarized<br>\u2022 Stamped (stamp duty payable via KRA)<br>\u2022 Registered with the lands registry within two months<br>\u2022 For POAs executed abroad: notarized at a Kenyan embassy or consulate<br><br>Be specific \u2014 a general POA grants broad authority. A specific POA limited to one transaction is safer.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'send-money-abroad',
        question: 'How do I send money for a property purchase from abroad?',
        answer:
          'Use secure, traceable channels only:<br><br>\u2022 <strong>Bank-to-bank wire transfer</strong> \u2014 preferably to your lawyer\u2019s client account (escrow), not directly to the seller\u2019s personal account<br>\u2022 <strong>International money transfer services</strong> \u2014 Wise, Remitly, or Western Union for smaller amounts<br>\u2022 <strong>Diaspora mortgage</strong> \u2014 several Kenyan banks (NCBA, Standard Chartered, KCB, Stanbic) offer mortgages to Kenyans abroad<br><br>Never send money via M-Pesa to a personal number for large transactions. Insist on a lawyer\u2019s client account as the receiving account \u2014 this provides legal protection if the transaction fails.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'diaspora-mortgages',
        question: 'Which banks offer mortgages to Kenyans in the diaspora?',
        answer:
          'Several Kenyan banks have dedicated diaspora mortgage products:<br><br>\u2022 <strong>NCBA</strong> \u2014 available in US, UK, and parts of Europe<br>\u2022 <strong>Standard Chartered Kenya</strong> \u2014 global reach through their international network<br>\u2022 <strong>KCB</strong> \u2014 diaspora banking services in multiple countries<br>\u2022 <strong>Stanbic Bank</strong> \u2014 particularly strong in Southern and East African diaspora<br><br>Requirements typically include: proof of income abroad (pay stubs, tax returns), valid Kenyan documents, credit history, and a larger deposit than local buyers (often 20\u201330% vs 10\u201320% locally). Interest rates and terms vary significantly, so compare at least three options.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'diaspora-avoid-scams',
        question: 'How do I avoid scams as a diaspora buyer?',
        answer:
          'The diaspora is a primary target for property fraud because buyers can\u2019t easily verify claims in person. Protect yourself:<br><br>1. <strong>Always conduct an official land search</strong> through Ardhisasa \u2014 never skip this<br>2. <strong>Hire your own lawyer</strong> \u2014 not one recommended by the seller<br>3. <strong>Never send money to personal accounts</strong> \u2014 use a lawyer\u2019s client account<br>4. <strong>Verify through multiple channels</strong> \u2014 cross-reference the seller\u2019s claims with independent sources<br>5. <strong>Visit or send a representative</strong> \u2014 photos and videos alone are not sufficient<br>6. <strong>Be wary of \u201Curgent\u201D deals</strong> \u2014 pressure to act fast is a classic scam tactic<br><br>If a deal seems too good to be true, it probably is. Nairobi property prices are well-established \u2014 a plot in Karen for KES\u00A05 million should raise immediate red flags.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 5. FOREIGN BUYERS
  // ─────────────────────────────────────────────
  {
    id: 'foreign-buyers',
    title: 'For Foreign Buyers',
    intro:
      'Non-Kenyan citizens can buy property in Kenya, but with important restrictions. Here\u2019s what the law allows \u2014 and what it doesn\u2019t.',
    items: [
      {
        id: 'foreigners-buy-property',
        question: 'Can foreigners buy property in Kenya?',
        answer:
          'Yes, but with restrictions. Under the 2010 Constitution, non-citizens can only hold land on a <strong>leasehold basis for a maximum of 99 years</strong>. Freehold ownership is reserved for Kenyan citizens.<br><br>Foreigners can purchase urban residential, commercial, and industrial property on leasehold terms. Agricultural land is prohibited for non-citizens. Companies with foreign ownership can hold property, but specific rules apply depending on the ownership percentage.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'foreigners-agricultural',
        question: 'Can foreigners buy agricultural land in Kenya?',
        answer:
          'No. The Land Control Act prohibits foreign nationals and companies with majority foreign ownership from purchasing agricultural land. This includes farmland, ranches, and rural plots designated as agricultural.<br><br>Exceptions require presidential approval via a Kenya Gazette notice \u2014 this is rarely granted. Some foreign investors structure purchases through Kenyan-majority-owned companies, but this requires careful legal structuring and compliance.<br><br>Consult a Kenyan property lawyer before pursuing any agricultural land investment as a non-citizen.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'lease-expires',
        question: 'What happens when a 99-year lease expires?',
        answer:
          'Leases can be renewed by applying to the National Land Commission before expiry. Renewal is standard practice and the 99-year limit is essentially long-term ownership in practical terms.<br><br>The renewal process involves an application, a review by the commission, and payment of a renewal fee. Begin the process well before your lease expires to ensure continuity of ownership.<br><br>Many of Nairobi\u2019s most valuable properties \u2014 including in Karen, Kilimani, and Westlands \u2014 are leasehold, and renewals are routine.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
      {
        id: 'company-ownership',
        question: 'Can I own property in Kenya through a company?',
        answer:
          'Yes. A Kenyan-registered company can hold freehold land. If the company has foreign shareholders, the ownership structure matters:<br><br>\u2022 <strong>Majority Kenyan-owned</strong> (51%+ Kenyan shareholders): can hold freehold<br>\u2022 <strong>Majority foreign-owned:</strong> restricted to leasehold (99 years max)<br>\u2022 <strong>Agricultural land:</strong> prohibited for companies with any foreign control<br><br>Company structures add complexity and cost (company registration, annual compliance, director requirements) but can provide flexibility for foreign investors. Professional legal advice is essential.',
        linkText: 'Browse verified properties on Afriqahome',
        linkHref: '/for-sale',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 6. SELLERS & AGENTS
  // ─────────────────────────────────────────────
  {
    id: 'sellers-agents',
    title: 'For Sellers & Agents',
    intro:
      'Whether you\u2019re listing a property or managing your Afriqahome agent profile, here\u2019s what you need to know.',
    items: [
      {
        id: 'how-to-list',
        question: 'How do I list my property on Afriqahome?',
        answer:
          'Create an agent account on Afriqahome, complete the verification process, and use the \u201CNew Listing\u201D dashboard to add your property. You\u2019ll need:<br><br>\u2022 Property type, location, and price<br>\u2022 Clear photos (minimum 5, ideally 10+)<br>\u2022 Accurate description including bedrooms, bathrooms, and amenities<br>\u2022 Title reference number for buyer confidence<br><br>Listing on the Ignite plan is free. Upgraded plans (Elevate and Dominate) offer additional tools including CRM features and branding options.',
        linkText: 'List your property',
        linkHref: '/agent/new-listing',
      },
      {
        id: 'listing-cost',
        question: 'How much does it cost to list on Afriqahome?',
        answer:
          'Afriqahome offers three agent plans:<br><br><strong>Ignite</strong> (Free) \u2014 Basic listing, lead notifications<br><strong>Elevate</strong> (KES\u00A06,000/month) \u2014 CRM tools, enhanced profile<br><strong>Dominate</strong> (KES\u00A018,000/month) \u2014 CRM, branding, team management<br><br><strong>Spotlight boost</strong> (KES\u00A02,000 per listing): gives your property premium placement for seven days.<br><br>Afriqahome is free for property buyers \u2014 no fees, no subscriptions, no hidden costs.',
        linkText: 'List your property',
        linkHref: '/agent/new-listing',
      },
      {
        id: 'listing-performance',
        question: 'What makes a listing perform well?',
        answer:
          'Listings with complete information and quality photos receive significantly more inquiries. Best practices:<br><br>\u2022 <strong>10+ clear, well-lit photos</strong> \u2014 include every room, exterior, and neighborhood context<br>\u2022 <strong>Accurate pricing</strong> \u2014 overpriced listings get filtered out; underpriced ones raise suspicion<br>\u2022 <strong>Complete details</strong> \u2014 bedrooms, bathrooms, plot size, parking, amenities, and nearby landmarks<br>\u2022 <strong>Honest description</strong> \u2014 mention both strengths and practical considerations<br>\u2022 <strong>Quick response</strong> \u2014 reply to inquiries within hours, not days',
        linkText: 'List your property',
        linkHref: '/agent/new-listing',
      },
      {
        id: 'respond-inquiries',
        question: 'How do I respond to buyer inquiries?',
        answer:
          'Buyer inquiries arrive through Afriqahome\u2019s messaging system and as notification alerts. Respond promptly \u2014 ideally within two hours during business hours. Include:<br><br>\u2022 Confirmation that the property is available<br>\u2022 Suggested viewing times<br>\u2022 Any additional information the buyer requested<br>\u2022 Your direct contact for scheduling<br><br>Buyers on Afriqahome have shown genuine interest by taking the time to inquire. Fast, helpful responses significantly increase your chances of converting inquiries into viewings.',
        linkText: 'Go to your agent dashboard',
        linkHref: '/agent-dashboard',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 7. ABOUT AFRIQAHOME
  // ─────────────────────────────────────────────
  {
    id: 'about-afriqahome',
    title: 'About Afriqahome',
    intro: 'What Afriqahome is, how it works, and what it costs.',
    items: [
      {
        id: 'what-is-afriqahome',
        question: 'What is Afriqahome?',
        answer:
          'Afriqahome is a real estate marketplace for Kenya. We connect property buyers with verified agents and listings across Nairobi and Kenya\u2019s major towns.<br><br>We\u2019re not an agency \u2014 we don\u2019t buy, sell, or own property. We provide the platform where buyers discover properties and agents reach serious buyers. Our focus is reducing the friction and trust gaps that make Kenya\u2019s property market difficult to navigate.',
        linkText: 'Browse verified properties',
        linkHref: '/for-sale',
      },
      {
        id: 'is-afriqahome-free',
        question: 'Is Afriqahome free for buyers?',
        answer:
          'Yes. Afriqahome is completely free for property buyers. You can browse listings, save properties, message agents, and use all buyer features without paying anything. We earn revenue from agent subscriptions and listing boosts \u2014 never from buyers.',
        linkText: 'Browse verified properties',
        linkHref: '/for-sale',
      },
      {
        id: 'does-afriqahome-sell',
        question: 'Does Afriqahome buy or sell properties?',
        answer:
          'No. Afriqahome is a marketplace, not a real estate agency. We connect buyers with agents and provide tools to make the search and communication process easier.<br><br>All transactions happen directly between buyers and agents/sellers. Afriqahome does not participate in negotiations, handle payments, or provide legal services. We recommend that all buyers engage an independent conveyancing lawyer for any property transaction.',
        linkText: 'About Afriqahome',
        linkHref: '/about',
      },
      {
        id: 'create-account',
        question: 'How do I create an account on Afriqahome?',
        answer:
          'Visit afriqahome.com and click \u201CSign Up.\u201D You can register with your email address. An account lets you save properties and message agents directly through the platform.<br><br>For agents, registration includes a verification step. Create your account, then follow the onboarding process to verify your identity and start listing properties.',
        linkText: 'Create your free account',
        // AGENT-28: signup mode, no role forced. This entry sits in the
        // "About Afriqahome" section and its answer covers buyers and agents
        // alike, so pinning role=agent here would push buyers into the wrong
        // role — the same defaulting bug this item exists to fix.
        linkHref: '/auth?mode=signup',
      },
      {
        id: 'contact-afriqahome',
        question: 'How can I contact Afriqahome?',
        answer:
          'You can reach us through:<br><br>\u2022 Email: support@afriqahome.com<br>\u2022 Our social media channels (links in the footer)<br><br>For urgent issues with a listing or suspected fraud, email us directly with the listing URL and we\u2019ll investigate promptly.',
        linkText: 'About Afriqahome',
        linkHref: '/about',
      },
    ],
  },
];

/**
 * Generate FAQPage JSON-LD schema from the FAQ data.
 * Call this in your SSR head to produce structured data.
 */
export function generateFAQSchema(): object {
  const allItems = FAQ_CATEGORIES.flatMap((cat) => cat.items);

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: allItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        // Strip HTML for schema — Google wants plain text
        text: item.answer.replace(/<[^>]*>/g, '').replace(/\u00A0/g, ' '),
      },
    })),
  };
}
